import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button, Form, Badge, Dropdown, Tabs, Tab } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaArrowLeft, FaPlus, FaTrash, FaCalendarAlt, FaExclamationTriangle, FaStar } from "react-icons/fa";
import './horaire.css';
import api from '../../../service/caller';
import { ToastContainer, toast } from 'react-toastify';
import WeekSelector from './WeekSelector';

const Horaire = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultScheduleTemplate = {
    Monday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Tuesday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Wednesday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Thursday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Friday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Saturday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
    Sunday: { start_time: "", end_time: "", duration_unit: 30, specialties: [], priority: "normal", emergency_slots: 0 },
  };

  const [scheduleWeeks, setScheduleWeeks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showhoraireModal, setShowhoraireModal] = useState(true);
  const [customDuration, setCustomDuration] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [scheduleId, setscheduleId] = useState(0);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [doctorSpecialties, setDoctorSpecialties] = useState([]);
  const [activeDaySettings, setActiveDaySettings] = useState(null);
  const [breakTimes, setBreakTimes] = useState({});
  const [currentDayForSettings, setCurrentDayForSettings] = useState("");
  const [showDaySettingsModal, setShowDaySettingsModal] = useState(false);

  const priorityOptions = [
    { value: "haute", label: "Haute priorité" },
    { value: "normale", label: "Priorité normale" },
    { value: "basse", label: "Basse priorité" },
  ];

  const queryParams = new URLSearchParams(location.search);
  const hospitalId = queryParams.get('hospital_id');
  const userId = queryParams.get('_id');

  // Traduire les jours en français
  const translateDay = (englishDay) => {
    const translations = {
      "Monday": "Lundi",
      "Tuesday": "Mardi",
      "Wednesday": "Mercredi",
      "Thursday": "Jeudi",
      "Friday": "Vendredi",
      "Saturday": "Samedi",
      "Sunday": "Dimanche"
    };
    return translations[englishDay] || englishDay;
  };

  // Fetch schedules and doctor specialties from the database
  useEffect(() => {
    const fetchUserScheduler = async () => {
      try {
        const response = await api.get(`api/schedule/${userId}`);
        const fetchedSchedules = response?.data?.data?.[0].schedules || [];
        setscheduleId(response?.data?.data?.[0]._id);

        // Fetch doctor specialties if available
        if (response?.data?.data?.[0].specialties) {
          setDoctorSpecialties(response?.data?.data?.[0].user.specialties);
          setAvailableSpecialties(response?.data?.data?.[0].user.specialties);
        }

        // Map fetched data to match the expected structure of `scheduleWeeks`
        const formattedSchedules = fetchedSchedules.map((schedule) => ({
          selectedWeek: {
            start_date: schedule.start_date,
            end_date: schedule.end_date,
          },
          scheduleData: schedule.schedule.reduce((acc, daySchedule) => {
            acc[daySchedule.day] = {
              start_time: daySchedule.start_time,
              end_time: daySchedule.end_time,
              duration_unit: daySchedule.duration_unit,
              specialties: daySchedule.specialties || [],
              priority: daySchedule.priority || "normale",
              emergency_slots: daySchedule.emergency_slots || 0,
              break_times: daySchedule.break_times || []
            };
            return acc;
          }, { ...defaultScheduleTemplate }),
        }));

        // Initialize with breakTimes if they exist
        const initialBreakTimes = {};
        formattedSchedules.forEach((week, weekIndex) => {
          Object.keys(week.scheduleData).forEach(day => {
            if (week.scheduleData[day].break_times) {
              initialBreakTimes[`${weekIndex}-${day}`] = week.scheduleData[day].break_times;
            } else {
              initialBreakTimes[`${weekIndex}-${day}`] = [];
            }
          });
        });
        setBreakTimes(initialBreakTimes);

        setScheduleWeeks(formattedSchedules.length ? formattedSchedules : [
          {
            selectedWeek: { start_date: '', end_date: '' },
            scheduleData: { ...defaultScheduleTemplate },
          },
        ]);

        setIsLoading(false);
      } catch (error) {
        toast.error("Erreur lors du chargement du planning");
        console.error("Erreur lors du chargement du planning:", error);
        setIsLoading(false);
      }
    };

    fetchUserScheduler();
  }, [userId]);

  const addNewWeekSchedule = () => {
    toast.warning('Veuillez définir la semaine avant de continuer!');

    setScheduleWeeks([
      ...scheduleWeeks,
      {
        selectedWeek: { start_date: '', end_date: '' },
        scheduleData: { ...defaultScheduleTemplate },
      },
    ]);
    setCurrentPage(scheduleWeeks.length); // Go to the new page
  };

  const removeWeekSchedule = (indexToRemove) => {
    if (scheduleWeeks.length > 1) {
      const updatedWeeks = scheduleWeeks.filter((_, index) => index !== indexToRemove);
      setScheduleWeeks(updatedWeeks);
      setCurrentPage((prevPage) => (prevPage > 0 ? prevPage - 1 : 0));
    }
  };

  const handleTimeChange = (weekIndex, day, type, value) => {
    const newScheduleWeeks = [...scheduleWeeks];
    newScheduleWeeks[weekIndex].scheduleData[day][type] = value;
    setScheduleWeeks(newScheduleWeeks);
  };

  const handleDurationChange = (weekIndex, day, duration) => {
    const newScheduleWeeks = [...scheduleWeeks];
    newScheduleWeeks[weekIndex].scheduleData[day].duration_unit =
      duration % 5 === 0 ? duration : 30;
    setScheduleWeeks(newScheduleWeeks);
  };

  const handleDefaultDuration = (duration) => {
    setScheduleWeeks((prevWeeks) =>
      prevWeeks.map((week) => ({
        ...week,
        scheduleData: Object.keys(week.scheduleData).reduce((acc, day) => {
          acc[day] = {
            ...week.scheduleData[day],
            duration_unit: duration,
          };
          return acc;
        }, {}),
      }))
    );
    setCustomDuration(`${duration} min`);
  };

  const handleEmergencySlots = (weekIndex, day, slots) => {
    const newScheduleWeeks = [...scheduleWeeks];
    newScheduleWeeks[weekIndex].scheduleData[day].emergency_slots = parseInt(slots);
    setScheduleWeeks(newScheduleWeeks);
  };

  const handlePriorityChange = (weekIndex, day, priority) => {
    const newScheduleWeeks = [...scheduleWeeks];
    newScheduleWeeks[weekIndex].scheduleData[day].priority = priority;
    setScheduleWeeks(newScheduleWeeks);
  };

  const handleSpecialtyToggle = (weekIndex, day, specialty) => {
    const newScheduleWeeks = [...scheduleWeeks];
    const dayData = newScheduleWeeks[weekIndex].scheduleData[day];
    
    if (dayData.specialties.includes(specialty)) {
      dayData.specialties = dayData.specialties.filter(s => s !== specialty);
    } else {
      dayData.specialties = [...dayData.specialties, specialty];
    }
    
    setScheduleWeeks(newScheduleWeeks);
  };

  const handleAddBreakTime = (day) => {
    const key = `${currentPage}-${day}`;
    const updatedBreakTimes = { ...breakTimes };
    
    updatedBreakTimes[key] = [...(breakTimes[key] || []), { 
      start: "", 
      end: "", 
      reason: "Pause"
    }];
    
    setBreakTimes(updatedBreakTimes);
  };

  const handleUpdateBreakTime = (day, index, field, value) => {
    const key = `${currentPage}-${day}`;
    const updatedBreakTimes = { ...breakTimes };
    
    if (!updatedBreakTimes[key]) {
      updatedBreakTimes[key] = [];
    }
    
    updatedBreakTimes[key][index] = {
      ...updatedBreakTimes[key][index],
      [field]: value
    };
    
    setBreakTimes(updatedBreakTimes);
  };

  const handleRemoveBreakTime = (day, index) => {
    const key = `${currentPage}-${day}`;
    const updatedBreakTimes = { ...breakTimes };
    
    updatedBreakTimes[key] = updatedBreakTimes[key].filter((_, i) => i !== index);
    
    setBreakTimes(updatedBreakTimes);
  };

  const openDaySettings = (day) => {
    setCurrentDayForSettings(day);
    setActiveDaySettings(scheduleWeeks[currentPage].scheduleData[day]);
    setShowDaySettingsModal(true);
  };

  const applyDaySettings = () => {
    const newScheduleWeeks = [...scheduleWeeks];
    newScheduleWeeks[currentPage].scheduleData[currentDayForSettings] = activeDaySettings;
    
    // Save break times
    const key = `${currentPage}-${currentDayForSettings}`;
    newScheduleWeeks[currentPage].scheduleData[currentDayForSettings].break_times = breakTimes[key] || [];
    
    setScheduleWeeks(newScheduleWeeks);
    setShowDaySettingsModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data including all the new fields
    const formData = {
      user: userId,
      hospital_id: hospitalId,
      specialties: doctorSpecialties,
      schedules: scheduleWeeks.map((week) => ({
        start_date: week.selectedWeek.start_date,
        end_date: week.selectedWeek.end_date,
        schedule: Object.keys(week.scheduleData).map((day) => {
          const dayData = week.scheduleData[day];
          const key = `${scheduleWeeks.indexOf(week)}-${day}`;
          
          return {
            day: day,
            start_time: dayData.start_time,
            end_time: dayData.end_time,
            duration_unit: dayData.duration_unit,
            specialties: dayData.specialties || [],
            priority: dayData.priority || "normale",
            emergency_slots: dayData.emergency_slots || 0,
            break_times: breakTimes[key] || []
          };
        }),
      })),
    };

    try {
      const response = await api.put(`api/schedule/${scheduleId}`, formData);
      if (response.status === 200 || response.status === 201) {
        toast.success('Planning mis à jour avec succès!');
        setTimeout(() => {
          navigate({
            pathname: '/hospitaladmin/staff_details',
            search: `?hospital_id=${response?.data?.data?.hospital_id}&_id=${response?.data?.data?.user}`,
          });
        }, 2000);
      } else {
        toast.error(response.data.message || 'Échec de la mise à jour du planning.');
      }
    } catch (error) {
      toast.error(
        `Échec de la mise à jour: ${
          error?.response?.data?.message || 'Une erreur inattendue est survenue.'
        }`
      );
    }
  };

  if (isLoading) {
    return <div>Chargement...</div>;
  }
 
  const handleUsersList = () => {
    navigate(-1);
  };

  return (
    <>
      <Modal show={showhoraireModal} backdrop="static" keyboard={false} centered size="lg">
        <Modal.Header>
          <Modal.Title className="mx-auto hius">Configuration du Planning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <button className="btn btn mb-4 toute" onClick={handleUsersList}>
              <FaArrowLeft /> Retour
          </button>
          <ToastContainer />
          <Form onSubmit={handleSubmit}>
            <div className="mb-4 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Planning Semaine {currentPage + 1}</h4>
                {scheduleWeeks.length > 1 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeWeekSchedule(currentPage)}
                  >
                    <FaTrash /> Supprimer
                  </Button>
                )}
              </div>
              <WeekSelector
                onWeekChange={(dates) => {
                  const newScheduleWeeks = [...scheduleWeeks];
                  newScheduleWeeks[currentPage].selectedWeek = dates;
                  setScheduleWeeks(newScheduleWeeks);
                }}
                selectedWeek={scheduleWeeks[currentPage].selectedWeek}
              />
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5>Durée de consultation par défaut</h5>
                  <div className="duration-buttons d-flex flex-wrap">
                    {[15, 20, 30, 45, 60].map((duration) => (
                      <Button
                        key={duration}
                        className={`mx-1 duration-button ${customDuration === `${duration} min` ? 'active' : ''}`}
                        onClick={() => handleDefaultDuration(duration)}
                        variant="outline-primary"
                        size="sm"
                      >
                        {duration} min
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              
              <table className="schedule-table">
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th className="text-center">Début</th>
                    <th className="text-center">Fin</th>
                    <th className="text-center">Spécialités</th>
                    <th className="text-center">Paramètres</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(scheduleWeeks[currentPage].scheduleData).map((day) => (
                    <tr key={day}>
                      <td>{translateDay(day)}</td>
                      <td className="text-center">
                        <input
                          type="time"
                          value={scheduleWeeks[currentPage].scheduleData[day].start_time}
                          onChange={(e) => handleTimeChange(currentPage, day, 'start_time', e.target.value)}
                          className="time-input"
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="time"
                          value={scheduleWeeks[currentPage].scheduleData[day].end_time}
                          onChange={(e) => handleTimeChange(currentPage, day, 'end_time', e.target.value)}
                          className="time-input"
                        />
                      </td>
                      <td className="text-center">
                        {scheduleWeeks[currentPage].scheduleData[day].specialties.length > 0 ? (
                          <div>
                            {scheduleWeeks[currentPage].scheduleData[day].specialties.slice(0, 1).map((spec, idx) => (
                              <Badge key={idx} bg="info" className="me-1">{spec}</Badge>
                            ))}
                            {scheduleWeeks[currentPage].scheduleData[day].specialties.length > 1 && (
                              <Badge bg="secondary">+{scheduleWeeks[currentPage].scheduleData[day].specialties.length - 1}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Non défini</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => openDaySettings(day)}
                        >
                          <FaCalendarAlt /> Configurer
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="text-center mb-3">
              <Button variant="outline-primary" onClick={addNewWeekSchedule}>
                <FaPlus /> Ajouter une autre semaine
              </Button>
            </div>
            
            <div className="d-flex justify-content-between mb-2">
              <Button
                variant="primary"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                Précédent
              </Button>
              <Button
                variant="primary"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, scheduleWeeks.length - 1))}
                disabled={currentPage === scheduleWeeks.length - 1}
              >
                Suivant
              </Button>
            </div>
            <Modal.Footer>
              <Button type="submit" variant="primary" className="mx-auto modify-button">
                Mettre à jour le planning
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal pour les paramètres détaillés du jour */}
      <Modal 
        show={showDaySettingsModal} 
        onHide={() => setShowDaySettingsModal(false)} 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Configuration détaillée - {translateDay(currentDayForSettings)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeDaySettings && (
            <Tabs defaultActiveKey="specialties" id="day-settings-tabs">
              <Tab eventKey="specialties" title="Spécialités">
                <div className="p-3">
                  <h5>Sélectionnez les spécialités disponibles ce jour</h5>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {availableSpecialties.map((specialty, index) => (
                      <Badge
                        key={index}
                        bg={activeDaySettings.specialties.includes(specialty) ? "primary" : "secondary"}
                        style={{ cursor: "pointer", padding: "8px" }}
                        onClick={() => {
                          setActiveDaySettings({
                            ...activeDaySettings,
                            specialties: activeDaySettings.specialties.includes(specialty)
                              ? activeDaySettings.specialties.filter(s => s !== specialty)
                              : [...activeDaySettings.specialties, specialty]
                          });
                        }}
                      >
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Tab>
              <Tab eventKey="priority" title="Priorités">
                <div className="p-3">
                  <h5>Niveau de priorité</h5>
                  <Form.Group className="mb-3">
                    <Form.Label>Définir la priorité pour ce jour</Form.Label>
                    <Form.Select 
                      value={activeDaySettings.priority}
                      onChange={(e) => setActiveDaySettings({
                        ...activeDaySettings,
                        priority: e.target.value
                      })}
                    >
                      {priorityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      La priorité détermine l'importance des rendez-vous ce jour-là.
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Durée de consultation (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="5"
                      step="5"
                      value={activeDaySettings.duration_unit}
                      onChange={(e) => setActiveDaySettings({
                        ...activeDaySettings,
                        duration_unit: parseInt(e.target.value)
                      })}
                    />
                  </Form.Group>
                </div>
              </Tab>
              <Tab eventKey="emergency" title="Urgences">
                <div className="p-3">
                  <h5>Créneaux d'urgence</h5>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre de créneaux réservés pour les urgences</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={activeDaySettings.emergency_slots}
                      onChange={(e) => setActiveDaySettings({
                        ...activeDaySettings,
                        emergency_slots: parseInt(e.target.value)
                      })}
                    />
                    <Form.Text className="text-muted">
                      Ces créneaux seront réservés pour des consultations urgentes.
                    </Form.Text>
                  </Form.Group>
                </div>
              </Tab>
              <Tab eventKey="breaks" title="Pauses">
                <div className="p-3">
                  <h5>Périodes de pause</h5>
                  <div className="mb-3">
                    {breakTimes[`${currentPage}-${currentDayForSettings}`]?.map((breakTime, index) => (
                      <div key={index} className="d-flex align-items-center mb-2 border p-2 rounded">
                        <div className="flex-grow-1 me-2">
                          <div className="d-flex mb-2">
                            <Form.Group className="me-2">
                              <Form.Label>Début</Form.Label>
                              <Form.Control
                                type="time"
                                value={breakTime.start}
                                onChange={(e) => handleUpdateBreakTime(currentDayForSettings, index, 'start', e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group>
                              <Form.Label>Fin</Form.Label>
                              <Form.Control
                                type="time"
                                value={breakTime.end}
                                onChange={(e) => handleUpdateBreakTime(currentDayForSettings, index, 'end', e.target.value)}
                              />
                            </Form.Group>
                          </div>
                          <Form.Group>
                            <Form.Label>Raison</Form.Label>
                            <Form.Control
                              type="text"
                              value={breakTime.reason}
                              onChange={(e) => handleUpdateBreakTime(currentDayForSettings, index, 'reason', e.target.value)}
                              placeholder="Ex: Déjeuner, Réunion"
                            />
                          </Form.Group>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveBreakTime(currentDayForSettings, index)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => handleAddBreakTime(currentDayForSettings)}
                      className="mt-2"
                    >
                      <FaPlus /> Ajouter une pause
                    </Button>
                  </div>
                </div>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDaySettingsModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={applyDaySettings}>
            Appliquer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Horaire;